<template>
  <el-dialog
    title="Вход по карте (Infin)"
    :visible.sync="localShow"
    :width="isMobile ? '95%' : '520px'"
    top="8vh"
    @open="onOpen"
    @close="onClose"
    custom-class="card-auth-dialog"
  >
    <div v-if="errorMsg" class="alert alert-danger" role="alert">
      <span class="alert-text">{{ errorMsg }}</span>
    </div>

    <p class="step-hint" v-if="stage !== 'done'">{{ stageHint }}</p>

    <p v-if="deviceProfile && stage !== 'card_start'" class="device-profile">
      <i class="ni ni-mobile-button"></i>
      Устройство: {{ deviceProfile.model }} · iOS {{ deviceProfile.ios_version }} · прил. {{ deviceProfile.app_version }}
    </p>

    <!-- 1. Card -->
    <div v-if="stage === 'card_start'">
      <base-input label="Номер карты (16 цифр)" placeholder="0000000000000000" v-model="pan" />
      <span v-if="pan && !validPan" class="field-err">16 цифр</span>
      <base-input label="Срок действия (ММГГ)" placeholder="0928" v-model="expiry" />
      <span v-if="expiry && !validExpiry" class="field-err">4 цифры ММГГ, месяц 01–12</span>

      <p class="device-note auto-note">
        <i class="ni ni-mobile-button"></i>
        Профиль устройства подставляется автоматически под реальный iPhone — вводить ничего не нужно.
      </p>
      <div class="device-toggle" @click="showDevice = !showDevice">
        <i :class="showDevice ? 'ni ni-bold-up' : 'ni ni-bold-down'"></i>
        Дополнительно
      </div>
      <div v-show="showDevice" class="device-section">
        <base-checkbox v-model="regenerateDevice">Сгенерировать новое устройство</base-checkbox>
        <base-checkbox v-model="manualDevice">Задать устройство вручную</base-checkbox>
        <div v-if="manualDevice" class="mt-2">
          <div class="row">
            <div class="col-6"><base-input label="Модель (deviceId)" placeholder="iPhone 16e" v-model="device.device_model" /></div>
            <div class="col-6"><base-input label="Версия iOS" placeholder="18.6.2" v-model="device.ios_version" /></div>
          </div>
          <div class="row">
            <div class="col-6"><base-input label="Serial" placeholder="UUID" v-model="device.device_serial" /></div>
            <div class="col-6"><base-input label="Версия приложения" placeholder="3.18.0" v-model="device.app_version" /></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. ONE_ID face (Luna SDK) -->
    <div v-else-if="stage === 'face'">
      <p class="device-note">
        Ветка ONE_ID: проверка лица (в приложении — Luna SDK). Камера формирует photoBase64.
        Банк может требовать именно штатный SDK — если снимок не принимается, вставьте результат SDK вручную.
      </p>
      <face-scanner v-model="photoBase64" :provider-id="providerId" @busy="faceProcessing = $event" />
    </div>

    <!-- 3. SMS -->
    <div v-else-if="stage === 'sms'">
      <p v-if="phoneMask" class="mask">Код отправлен на {{ phoneMask }}</p>
      <base-input label="SMS-код" placeholder="012345" v-model="smsCode" />
      <span v-if="smsCode && !validSms" class="field-err">4–8 цифр</span>
      <base-checkbox v-if="trustAvailable" v-model="trustDevice">Доверять этому устройству</base-checkbox>
      <a class="resend-link" @click="resend" :class="{ disabled: loading }">Отправить код повторно</a>
    </div>

    <!-- 4. Done -->
    <div v-else-if="stage === 'done'" class="done-box">
      <i class="ni ni-check-bold"></i>
      <p>Вход по карте завершён — токен получен.</p>
    </div>

    <span slot="footer" class="dialog-footer">
      <base-button type="secondary" @click="closeModal">Закрыть</base-button>

      <base-button v-if="stage === 'card_start'" type="primary" :disabled="loading || !validCard" @click="start">
        <i v-if="loading" class="el-icon-loading"></i> Начать вход
      </base-button>
      <base-button v-else-if="stage === 'face'" type="primary" :disabled="loading || faceProcessing || !photoBase64" @click="submit('face', photoBase64)">
        <i v-if="loading" class="el-icon-loading"></i> Отправить
      </base-button>
      <base-button v-else-if="stage === 'sms'" type="primary" :disabled="loading || !validSms" @click="submitSms">
        <i v-if="loading" class="el-icon-loading"></i> Подтвердить
      </base-button>
      <base-button v-if="attemptId" type="warning" :disabled="loading" @click="restart">Начать заново</base-button>
    </span>
  </el-dialog>
</template>

<script>
import infinAuthRequests from '@/utils/infinAuthRequests';
import BaseInput from '@/components/Inputs/BaseInput.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseCheckbox from '@/components/Inputs/BaseCheckbox.vue';
import FaceScanner from '@/components/Modals/FaceScanner.vue';

const ERROR_TEXT = {
  DEVICE_CONFIG_REQUIRED: 'Не удалось подготовить профиль устройства.',
  API_DECLINED: 'Банк отклонил запрос (status 3999). Попробуйте позже — причина не раскрывается.',
  UNSUPPORTED_LOGIN_TYPE: 'Сервер выбрал неподдерживаемую ветку входа.',
  NO_TOKEN: 'Сервер не вернул токен доступа.',
  VERIFY_FAILED: 'Проверка токена (customer/data) не прошла.',
  HTTP_ERROR: 'Ошибка запроса к банку.',
  STATUS_ERROR: 'Банк вернул ошибку.',
  BAD_RESPONSE: 'Некорректный ответ банка.',
  STEP_MISMATCH: 'Этот шаг сейчас не ожидается сервером.',
  NATIVE_AUTH_DISABLED: 'Вход недоступен для этого провайдера.',
  AUTH_IN_PROGRESS: 'Дождитесь завершения текущего запроса Infin.',
  AUTH_ATTEMPT_ACTIVE: 'Уже существует незавершённая попытка входа.',
  AUTH_OUTCOME_UNKNOWN: 'Результат запроса неизвестен. Повтор временно запрещён.',
  ATTEMPT_MISMATCH: 'Это окно относится к другой попытке. Откройте вход заново.',
  ATTEMPT_EXPIRED: 'Попытка истекла. Начните вход заново.',
};

const STAGE_HINT = {
  card_start: 'Шаг 1. Карта и срок действия',
  face: 'Проверка лица (ONE_ID)',
  sms: 'Подтвердите SMS-код',
};

export default {
  name: 'CardAuthModal',
  mixins: [infinAuthRequests],
  components: { BaseInput, BaseButton, BaseCheckbox, FaceScanner },
  props: {
    show: { type: Boolean, default: false },
    providerId: { type: Number, default: null },
  },
  data() {
    return {
      stage: 'card_start',
      errorCode: '',
      errorMsg: '',
      pan: '',
      expiry: '',
      photoBase64: '',
      faceProcessing: false,
      smsCode: '',
      trustDevice: false,
      trustAvailable: false,
      loginType: null,
      phoneMask: '',
      deviceProfile: null,
      showDevice: false,
      manualDevice: false,
      regenerateDevice: false,
      device: { device_model: '', device_serial: '', ios_version: '', app_version: '' },
    };
  },
  computed: {
    localShow: {
      get() { return this.show; },
      set(v) { this.$emit('update:show', v); },
    },
    isMobile() { return window.innerWidth < 576; },
    stageHint() { return STAGE_HINT[this.stage] || ''; },
    validPan() { return /^\d{16}$/.test(this.pan); },
    validExpiry() {
      if (!/^\d{4}$/.test(this.expiry)) return false;
      const mm = +this.expiry.slice(0, 2);
      return mm >= 1 && mm <= 12;
    },
    validCard() { return this.validPan && this.validExpiry; },
    validSms() { return /^\d{4,8}$/.test(this.smsCode); },
  },
  watch: {
    pan(v) { const s = (v || '').replace(/\D/g, '').slice(0, 16); if (s !== v) this.pan = s; },
    expiry(v) { const s = (v || '').replace(/\D/g, '').slice(0, 4); if (s !== v) this.expiry = s; },
    smsCode(v) { const s = (v || '').replace(/\D/g, '').slice(0, 8); if (s !== v) this.smsCode = s; },
    'device.ios_version'(v) { const s = (v || '').replace(/[^\d.]/g, '').slice(0, 10); if (s !== v) this.device.ios_version = s; },
    'device.app_version'(v) { const s = (v || '').replace(/[^\d.]/g, '').slice(0, 10); if (s !== v) this.device.app_version = s; },
  },
  methods: {
    resetAuthForm() {
      this.stage = 'card_start';
      this.errorCode = '';
      this.errorMsg = '';
      this.photoBase64 = '';
      this.faceProcessing = false;
      this.smsCode = '';
      this.trustDevice = false;
      this.trustAvailable = false;
      this.loginType = null;
      this.phoneMask = '';
      this.deviceProfile = null;
    },

    async start() {
      this.clearError();
      const payload = { pan: this.pan, expiry: this.expiry };
      if (this.manualDevice) {
        payload.device = {
          device_model: this.device.device_model.trim(),
          device_serial: this.device.device_serial.trim(),
          ios_version: this.device.ios_version.trim(),
          app_version: this.device.app_version.trim(),
        };
      } else if (this.regenerateDevice) {
        payload.regenerate_device = true;
      }
      await this.post(`dashboard/providers/${this.providerId}/card-auth/start`, payload, true);
    },

    async submit(screen, value) {
      this.clearError();
      await this.post(`dashboard/providers/${this.providerId}/card-auth/step`, {
        screen, pan: this.pan, expiry: this.expiry, value,
      });
    },

    async submitSms() {
      this.clearError();
      await this.post(`dashboard/providers/${this.providerId}/card-auth/step`, {
        screen: 'sms', pan: this.pan, expiry: this.expiry, value: this.smsCode,
        trust_device: this.trustAvailable ? this.trustDevice : null,
      });
    },

    async resend() {
      if (this.loading) return;
      this.clearError();
      await this.post(`dashboard/providers/${this.providerId}/card-auth/step`, {
        screen: 'resend_sms', pan: this.pan, expiry: this.expiry,
      });
    },

    handleResult(data, isStart) {
      if (!data) { this.setError('HTTP_ERROR'); return; }
      if (data.phone_mask) this.phoneMask = data.phone_mask;
      if (data.login_type) this.loginType = data.login_type;
      if (typeof data.trust_available === 'boolean') this.trustAvailable = data.trust_available;
      if (data.device_profile && data.device_profile.model) this.deviceProfile = data.device_profile;

      if (data.status === true) {
        if (data.done) {
          if (isStart) { this.stage = 'done'; return; }
          this.completeAuthView();
          return;
        }
        if (data.screen) this.stage = data.screen;
        return;
      }
      this.setError(data.error, data.message);
    },

    setError(code, serverMsg) {
      this.errorCode = code || 'HTTP_ERROR';
      const base = ERROR_TEXT[this.errorCode] || 'Ошибка входа.';
      this.errorMsg = serverMsg ? `${base} (${serverMsg})` : base;
    },
    clearError() { this.errorCode = ''; this.errorMsg = ''; },
    closeModal() { this.localShow = false; },
  },
};
</script>

<style scoped>
.step-hint { font-weight: 600; color: #32325d; margin-bottom: 1rem; }
.device-note { font-size: 0.78rem; color: #8898aa; margin-bottom: 0.5rem; }
.auto-note { background: #eef2ff; border-radius: 0.375rem; padding: 0.6rem 0.75rem; color: #4b5bbf; margin: 0.75rem 0; }
.device-toggle { cursor: pointer; color: #5e72e4; font-size: 0.85rem; margin: 0.5rem 0; user-select: none; }
.device-section { background: #f7fafc; border-radius: 0.375rem; padding: 0.75rem; margin-bottom: 0.5rem; }
.device-profile { font-size: 0.82rem; color: #525f7f; background: #f7fafc; border-radius: 0.375rem; padding: 0.5rem 0.75rem; margin-bottom: 0.9rem; }
.field-err { display: block; color: #f5365c; font-size: 0.72rem; margin: -0.5rem 0 0.6rem; }
.mask { font-size: 0.85rem; color: #525f7f; margin-bottom: 0.5rem; }
.resend-link { display: inline-block; margin-top: 0.5rem; color: #5e72e4; cursor: pointer; font-size: 0.85rem; }
.resend-link.disabled { color: #adb5bd; pointer-events: none; }
.done-box { text-align: center; padding: 1.5rem 0; color: #2dce89; }
.done-box i { font-size: 2.5rem; }
.dialog-footer { display: flex; justify-content: flex-end; gap: 0.5rem; flex-wrap: wrap; }
.alert { margin-bottom: 1rem; }
@media (max-width: 576px) {
  .dialog-footer { flex-direction: column-reverse; }
  .dialog-footer button { width: 100%; margin: 0 !important; }
}
</style>
